import React, { useEffect, useRef, useState } from 'react';
import { Alert, Button, Form, Modal, Nav, Spinner, Tab } from 'react-bootstrap';
import axios from 'axios';
import CustomEditor from './Editor';
import { baseURL } from '../helpers/baseURL';
import { headers, getRequestErrorMessage } from '../helpers/Requests';
import successAlert from '../helpers/successAlert';
import { QUICK_REPLY_VARIABLES, quickReplyPlaceholder } from '../helpers/quickReplyVariables';
import { formatDateTime } from '../helpers/formatDate';
import styles from './QuickReplyModal.module.css';

const QuickReplyModal = ({ show, onHide, quickReply, onSaved }) => {
    const editorRef = useRef(null);
    const mouseDownInsideRef = useRef(false);
    const mouseUpInsideRef = useRef(false);
    const [activeTab, setActiveTab] = useState('general');
    const [name, setName] = useState('');
    const [isActive, setIsActive] = useState(true);
    const [isLoading, setIsLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [insertedVariableKey, setInsertedVariableKey] = useState(null);
    const insertedVariableTimeoutRef = useRef(null);
    const isEdit = Boolean(quickReply?.id);

    useEffect(() => {
        if (!show) {
            return;
        }
        setActiveTab('general');
        setErrorMessage('');
        setName(quickReply?.name ?? '');
        setIsActive(quickReply?.is_active ?? true);
        setTimeout(() => {
            editorRef.current?.setHTML(quickReply?.content ?? '');
        }, 0);
    }, [show, quickReply]);

    useEffect(() => {
        if (!show) {
            return undefined;
        }

        const isInsideDialog = (target) => !!target?.closest?.('.modal-dialog');

        const onMouseDown = (event) => {
            mouseDownInsideRef.current = isInsideDialog(event.target);
        };
        const onMouseUp = (event) => {
            mouseUpInsideRef.current = isInsideDialog(event.target);
        };

        document.addEventListener('mousedown', onMouseDown, true);
        document.addEventListener('mouseup', onMouseUp, true);

        return () => {
            document.removeEventListener('mousedown', onMouseDown, true);
            document.removeEventListener('mouseup', onMouseUp, true);
        };
    }, [show]);

    useEffect(() => () => {
        if (insertedVariableTimeoutRef.current) {
            clearTimeout(insertedVariableTimeoutRef.current);
        }
    }, []);

    const handleHide = (event) => {
        if (event?.key === 'Escape' || event?.type === 'keydown') {
            onHide();
            return;
        }

        // Ignore backdrop dismiss only when the user dragged from inside the modal to outside
        // (e.g. selecting text) — not for normal clicks on X, Cancel, or backdrop.
        if (mouseDownInsideRef.current && !mouseUpInsideRef.current) {
            return;
        }

        onHide();
    };

    const formatCreatedMeta = () => {
        if (!isEdit || !quickReply?.created_by) {
            return null;
        }
        const raw = quickReply.created_at_raw ?? quickReply.created_at;
        const dateLabel = raw ? formatDateTime(raw) : null;
        return (
            <div className="text-muted small mt-3">
                Created by {quickReply.created_by.name}
                {dateLabel ? ` · ${dateLabel}` : ''}
            </div>
        );
    };

    const insertVariable = (key) => {
        const placeholder = quickReplyPlaceholder(key);
        editorRef.current?.insertText(placeholder, { flash: true });
        setActiveTab('general');

        if (insertedVariableTimeoutRef.current) {
            clearTimeout(insertedVariableTimeoutRef.current);
        }
        setInsertedVariableKey(key);
        insertedVariableTimeoutRef.current = setTimeout(() => setInsertedVariableKey(null), 2000);
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        setErrorMessage('');

        const content = editorRef.current?.getHTML() ?? '';
        if (!name.trim()) {
            setErrorMessage('Please enter a name for this quick reply.');
            return;
        }
        if (!editorRef.current || editorRef.current.getText().trim().length === 0) {
            setErrorMessage('Please enter reply content.');
            return;
        }

        const payload = {
            name: name.trim(),
            content,
            is_active: isActive,
        };

        setIsLoading(true);
        try {
            const url = isEdit
                ? `${baseURL}quick-reply-update/${quickReply.id}`
                : `${baseURL}quick-reply`;
            const response = await axios.post(url, payload, headers);
            successAlert(response?.data?.message ?? 'Quick reply saved.');
            onSaved?.(response?.data?.quick_reply);
            onHide();
        } catch (error) {
            setErrorMessage(getRequestErrorMessage(error, 'Failed to save quick reply.'));
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <Modal show={show} onHide={handleHide} size="lg" centered>
            <Modal.Header>
                <Modal.Title>{isEdit ? 'Edit Quick Reply' : 'Create Quick Reply'}</Modal.Title>
                <button
                    type="button"
                    className="btn-close"
                    aria-label="Close"
                    onClick={onHide}
                />
            </Modal.Header>
            <Form onSubmit={handleSubmit}>
                <Modal.Body>
                    {errorMessage && <Alert variant="danger">{errorMessage}</Alert>}

                    <Tab.Container activeKey={activeTab} onSelect={(key) => setActiveTab(key || 'general')}>
                        <Nav variant="tabs" className="mb-3">
                            <Nav.Item>
                                <Nav.Link eventKey="general" style={{ color: 'black' }}>General</Nav.Link>
                            </Nav.Item>
                            <Nav.Item>
                                <Nav.Link eventKey="variables" style={{ color: 'black' }}>Variables</Nav.Link>
                            </Nav.Item>
                        </Nav>

                        <Tab.Content>
                            <Tab.Pane eventKey="general">
                                <Form.Group className="mb-3">
                                    <Form.Label>Name</Form.Label>
                                    <Form.Control
                                        value={name}
                                        onChange={(e) => setName(e.target.value)}
                                        placeholder='e.g. "Out of office"'
                                        maxLength={255}
                                    />
                                </Form.Group>

                                <Form.Group className="mb-3">
                                    <Form.Label>Reply content</Form.Label>
                                    <CustomEditor
                                        ref={editorRef}
                                        id={`quick-reply-editor-${quickReply?.id ?? 'new'}`}
                                        placeholder="Hi {{customer_name}}, thank you for your message..."
                                    />
                                </Form.Group>

                                <Form.Check
                                    type="switch"
                                    id="quick-reply-active"
                                    label="Active"
                                    checked={isActive}
                                    onChange={(e) => setIsActive(e.target.checked)}
                                />

                                {formatCreatedMeta()}
                            </Tab.Pane>

                            <Tab.Pane eventKey="variables">
                                <p className="text-muted small">
                                    Click a variable to insert it into your reply. Variables are replaced with real values when an agent uses the quick reply on a ticket.
                                </p>
                                <div className={styles.variableList}>
                                    {QUICK_REPLY_VARIABLES.map((variable) => (
                                        <button
                                            key={variable.key}
                                            type="button"
                                            className={`${styles.variableItem} ${insertedVariableKey === variable.key ? styles.variableItemAdded : ''}`}
                                            onClick={() => insertVariable(variable.key)}
                                        >
                                            <code>{quickReplyPlaceholder(variable.key)}</code>
                                            <span className={styles.variableLabel}>
                                                {variable.label}
                                                {insertedVariableKey === variable.key && (
                                                    <span className={styles.variableAddedBadge}>Added</span>
                                                )}
                                            </span>
                                            <span className={styles.variableDescription}>{variable.description}</span>
                                        </button>
                                    ))}
                                </div>
                            </Tab.Pane>
                        </Tab.Content>
                    </Tab.Container>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={onHide} disabled={isLoading}>
                        Cancel
                    </Button>
                    <Button type="submit" disabled={isLoading}>
                        {isLoading ? <Spinner size="sm" animation="border" /> : (isEdit ? 'Save changes' : 'Create quick reply')}
                    </Button>
                </Modal.Footer>
            </Form>
        </Modal>
    );
};

export default QuickReplyModal;
